import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Footer() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const footerCategories = categories.slice(8, 14);

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus(null);
    setFormError(null);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("phone_alt") as string || "").trim();
    if (honeypot) {
      setFormStatus("success");
      form.reset();
      return;
    }

    const email = (formData.get("email") as string || "").trim();
    if (!email) {
      setFormError(t("footer.newsletter.error.empty"));
      return;
    }

    formData.delete("phone_alt");

    try {
      const response = await fetch("https://readdy.ai/api/form/d96h17vpg5pqhofcuoug", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(formData as unknown as URLSearchParams).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not json
      }

      if (response.ok && parsed && parsed.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const serverMsg = (parsed as Record<string, unknown> | null)?.["meta"] as Record<string, unknown> | undefined;
        const msg = serverMsg?.["message"] as string || serverMsg?.["detail"] as string || responseText;
        setFormError(msg || t("footer.newsletter.error.general"));
      }
    } catch {
      setFormError(t("footer.newsletter.error.general"));
    }
  };

  return (
    <footer className="bg-accent-900/90">
      <div className="px-4 md:px-6 py-14 md:py-16">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          <div className="lg:w-2/5">
            <Link
              to="/"
              className="font-heading text-2xl font-bold tracking-tight text-white whitespace-nowrap"
            >
              kakobuy<span className="text-accent-300">sheet</span>
            </Link>
            <p className="mt-4 text-accent-100/80 text-sm leading-relaxed max-w-sm">
              {t("footer.tagline")}
            </p>

            <form
              className="mt-6 flex gap-2 max-w-sm"
              onSubmit={handleNewsletterSubmit}
              data-readdy-form=""
            >
              <input
                type="email"
                name="email"
                placeholder={t("footer.newsletter.placeholder")}
                required
                className="flex-1 px-4 py-2.5 rounded-lg bg-accent-800/50 border border-accent-600/40 text-white text-sm placeholder:text-accent-300/60 outline-none focus:border-accent-400 transition-colors"
              />
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />
              <button
                type="submit"
                className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500 text-background-50 hover:bg-accent-400 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-notification-3-line text-lg"></i>
              </button>
            </form>

            {formStatus === "success" && (
              <p className="mt-3 text-accent-200 text-sm">{t("footer.newsletter.success")}</p>
            )}
            {formError && (
              <p className="mt-3 text-red-300 text-sm">{formError}</p>
            )}
            {!formStatus && !formError && (
              <p className="mt-3 text-accent-200/60 text-sm">
                {t("footer.promo")}
              </p>
            )}
          </div>

          <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <h4 className="text-white font-semibold text-sm mb-4 whitespace-nowrap">
                {t("footer.quick.links")}
              </h4>
              <div className="space-y-2.5">
                <Link
                  to="/"
                  className="block text-accent-100/70 text-sm hover:text-white transition-colors whitespace-nowrap"
                >
                  {t("nav.home")}
                </Link>
                <Link
                  to="/guides/complete-kakobuy-spreadsheet-guide"
                  className="block text-accent-100/70 text-sm hover:text-white transition-colors whitespace-nowrap"
                >
                  {t("nav.guides")}
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="block text-accent-100/70 text-sm hover:text-white transition-colors whitespace-nowrap"
                >
                  {t("nav.contact")}
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-white font-semibold text-sm mb-4 whitespace-nowrap">
                {t("footer.categories")}
              </h4>
              <div className="space-y-2.5">
                {categories.slice(0, 6).map((cat) => (
                  <a
                    key={cat.nameKey}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block text-accent-100/70 text-sm hover:text-white transition-colors whitespace-nowrap"
                  >
                    {t(cat.nameKey)}
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-white font-semibold text-sm mb-4 whitespace-nowrap">
                {t("footer.categories")}
              </h4>
              <div className="space-y-2.5">
                {categories.slice(6, 11).map((cat) => (
                  <a
                    key={cat.nameKey}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block text-accent-100/70 text-sm hover:text-white transition-colors whitespace-nowrap"
                  >
                    {t(cat.nameKey)}
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-white font-semibold text-sm mb-4 whitespace-nowrap">
                {t("footer.categories")}
              </h4>
              <div className="space-y-2.5">
                {footerCategories.map((cat) => (
                  <a
                    key={cat.nameKey}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block text-accent-100/70 text-sm hover:text-white transition-colors whitespace-nowrap"
                  >
                    {t(cat.nameKey)}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-accent-700/50">
        <div className="px-4 md:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-accent-200/60 text-xs whitespace-nowrap">
            &copy; {new Date().getFullYear()} kakobuyspreadsheet.art — {t("footer.copyright")}
          </p>
          <div className="flex items-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-8 h-8 flex items-center justify-center rounded-md border border-accent-600/50 text-accent-200/70 hover:text-white hover:border-accent-400 transition-colors"
            >
              <i className="ri-twitter-x-line text-sm"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-8 h-8 flex items-center justify-center rounded-md border border-accent-600/50 text-accent-200/70 hover:text-white hover:border-accent-400 transition-colors"
            >
              <i className="ri-instagram-line text-sm"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-8 h-8 flex items-center justify-center rounded-md border border-accent-600/50 text-accent-200/70 hover:text-white hover:border-accent-400 transition-colors"
            >
              <i className="ri-pinterest-line text-sm"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="w-8 h-8 flex items-center justify-center rounded-md border border-accent-600/50 text-accent-200/70 hover:text-white hover:border-accent-400 transition-colors"
            >
              <i className="ri-youtube-line text-sm"></i>
            </a>
          </div>
          <a
            href="#"
            className="text-accent-200/60 text-xs hover:text-white transition-colors whitespace-nowrap"
          >
            {t("footer.privacy")}
          </a>
        </div>
      </div>
    </footer>
  );
}